import { memo, useMemo } from 'react';
import { MoreIcon } from 'src/modules/common/icons/MoreIcon';
import { CaretLeftIcon } from 'src/modules/common/icons/CaretLeftIcon';
import { CaretRightIcon } from 'src/modules/common/icons/CaretRightIcon';
import clsx from 'clsx';
import { DOTS } from 'src/modules/common/constants';
import { usePagination } from 'src/modules/common/hooks/usePagination.ts';

type Props = {
    total: number;
    isMobile: boolean;
    currentPage: number;
    pageSize: number;
    onPageChange: (page: number) => void;
}

export const Pagination = memo(({ total, isMobile, currentPage, pageSize, onPageChange }: Props) => {
    const paginationData = usePagination({ total, pageSize, currentPage, siblingCount: isMobile ? 0 : 1 });

    const pageIndexBeforeDots = useMemo(
        () => paginationData.findIndex((page) => page === DOTS),
        [paginationData],
    );

    const forwardButtonDisabled = useMemo(() => {
        const lastPage = paginationData[paginationData.length - 1];
        return lastPage === currentPage;
    }, [paginationData, currentPage]);

    return (
        <div className="vss-pagination-container">
            <div className="vss-pagination">
                <button
                    type="button"
                    className={clsx({
                        'vss-pagination__button': true,
                        'vss-pagination__button--disabled': currentPage === 1,
                    })}
                    disabled={currentPage === 1}
                    onClick={() => onPageChange(currentPage - 1)}
                >
                    <div className="vss-pagination__button-icon">
                        <CaretLeftIcon/>
                    </div>
                </button>

                {paginationData.map((page, i) => {
                    if (page === DOTS) {
                        return (
                            <button
                                // eslint-disable-next-line react/no-array-index-key
                                key={page + i}
                                type="button"
                                className="vss-pagination__button"
                                onClick={() => onPageChange(pageIndexBeforeDots + 1)}
                            >
                                <div className="vss-pagination__button-icon">
                                    <MoreIcon orientation="horizontal"/>
                                </div>
                            </button>
                        );
                    }

                    return (
                        <button
                            key={page}
                            type="button"
                            className={clsx({
                                'vss-pagination__button': true,
                                'vss-pagination__button--selected': page === currentPage,
                            })}
                            disabled={page === currentPage}
                            onClick={() => onPageChange(page)}
                        >
                            <span className={clsx({
                                'vss-pagination__button-text': true,
                                'vss-pagination__button-text--selected': page === currentPage,
                            })}
                            >
                                {page}
                            </span>
                        </button>
                    );
                })}

                <button
                    type="button"
                    className={clsx({
                        'vss-pagination__button': true,
                        'vss-pagination__button--disabled': forwardButtonDisabled,
                    })}
                    disabled={forwardButtonDisabled}
                    onClick={() => onPageChange(currentPage + 1)}
                >
                    <div className="vss-pagination__button-icon">
                        <CaretRightIcon/>
                    </div>
                </button>
            </div>
        </div>
    );
});
